const TIME_ZONE = 'America/Merida';

const upper = value => String(value ?? '').trim().toUpperCase();

export const PERSONNEL_STATUS_COLORS = {
  laborando: '#16a34a',
  jornada_concluida: '#16a34a',
  salida_anticipada: '#eab308',
  retardo: '#eab308',
  jornada_extendida: '#f97316',
  cierre_administrativo: '#9333ea',
  vacaciones: '#2563eb',
  incapacidad: '#ec4899',
  permiso: '#ec4899',
  suspendido: '#000000',
  fuera_jornada: '#d1d5db'
};

const status = (key, label, detail, extra = {}) => ({
  key,
  label,
  color: PERSONNEL_STATUS_COLORS[key] || '#d1d5db',
  icon: extra.icon || '⚪',
  detail,
  ...extra
});

export const getMeridaParts = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(date);
  const out = {};
  parts.forEach(({ type, value: partValue }) => { if (type !== 'literal') out[type] = partValue; });
  const dateKey = `${out.year}-${out.month}-${out.day}`;
  return {
    dateKey,
    minutes: Number(out.hour) * 60 + Number(out.minute),
    dayOfWeek: new Date(`${dateKey}T12:00:00Z`).getUTCDay(),
    hour: Number(out.hour),
    minute: Number(out.minute)
  };
};

const dayCode = day => day === 0 ? '7' : String(day);

const isDateInRange = (today, start, end) => {
  if (!start || !end) return false;
  return today >= String(start).slice(0, 10) && today <= String(end).slice(0, 10);
};

const normalizeAttendance = marks => (Array.isArray(marks) ? marks : [])
  .filter(mark => mark && (mark.type === 'Entrada' || mark.type === 'Salida'))
  .filter(mark => mark.isEffectiveForCalculation !== false)
  .map(mark => ({ ...mark, _time: new Date(mark.timestamp).getTime() }))
  .filter(mark => Number.isFinite(mark._time))
  .sort((a, b) => a._time - b._time || Number(a.id || 0) - Number(b.id || 0));

const findActiveLeave = (user, today) => {
  const rows = Array.isArray(user?.activeLeaves) ? user.activeLeaves : [];
  return rows.find(row => upper(row.status) === 'APROBADA' && isDateInRange(today, row.startDate, row.endDate)) || null;
};

const getSchedule = user => {
  if (user?.scheduleAssignment?.schedule) return user.scheduleAssignment.schedule;
  if (user?.scheduleAssignment && (user.scheduleAssignment.startTime || user.scheduleAssignment.endTime)) return user.scheduleAssignment;
  if (user?.workSchedule) return user.workSchedule;
  return null;
};

const parseTime = value => {
  const text = String(value || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(text)) return null;
  const hours = Number(text.slice(0, 2));
  const minutes = Number(text.slice(3, 5));
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
};

const wallMinutes = (dateKey, minutes) => {
  const date = new Date(`${dateKey}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor(date.getTime() / 60000) + minutes;
};

const scheduleWindowForEntry = (schedule, entry) => {
  if (!schedule) return null;
  const startMin = parseTime(schedule.startTime);
  const endMin = parseTime(schedule.endTime);
  if (startMin === null || endMin === null || upper(schedule.type) === 'FLEXIBLE') return null;

  const entryParts = getMeridaParts(entry.timestamp);
  if (!entryParts) return null;
  const workDays = String(schedule.workDays || '').split(',').map(x => x.trim()).filter(Boolean);
  if (workDays.length && !workDays.includes(dayCode(entryParts.dayOfWeek))) return null;

  const crossesMidnight = Boolean(schedule.crossesMidnight) || endMin <= startMin;
  const startWall = wallMinutes(entryParts.dateKey, startMin);
  const endWall = wallMinutes(entryParts.dateKey, endMin) + (crossesMidnight ? 1440 : 0);
  const startTolerance = Math.max(0, Number(schedule.toleranceEntryMinutes ?? schedule.toleranceMinutes ?? 0));
  const exitTolerance = Math.max(0, Number(schedule.toleranceExitMinutes ?? schedule.toleranceMinutes ?? 0));
  return {
    startMin, endMin, crossesMidnight, startTolerance, exitTolerance,
    startWall, endWall,
    startAllowedWall: startWall + startTolerance,
    endAllowedWall: endWall - exitTolerance
  };
};

const findAttendanceState = marks => {
  let openEntry = null;
  let latestPair = null;
  for (const mark of marks) {
    if (mark.type === 'Entrada') {
      openEntry = mark;
    } else if (mark.type === 'Salida' && openEntry) {
      latestPair = { entry: openEntry, exit: mark };
      openEntry = null;
    }
  }
  return { openEntry, latestPair };
};

const findClosureForEntry = (user, entry) => {
  if (!entry) return null;
  const rows = Array.isArray(user?.jornadaClosures) ? user.jornadaClosures : [];
  return rows.find(row => Number(row.entryAttendanceId) === Number(entry.id)) || null;
};

// Determina si una marca/jornada todavía pertenece al día laboral que estamos
// mostrando. Esto evita que una Jornada concluida de ayer quede "pegada" en
// el monitor de hoy. Una excepción válida es una jornada fija/nocturna que
// comenzó ayer y terminó hoy.
const attendanceBelongsToCurrentWorkday = (attendance, nowParts, schedule) => {
  if (!attendance || !nowParts) return false;
  const attendanceParts = getMeridaParts(attendance.timestamp);
  if (!attendanceParts) return false;

  if (attendanceParts.dateKey === nowParts.dateKey) return true;

  const startMin = parseTime(schedule?.startTime);
  const endMin = parseTime(schedule?.endTime);
  const fixedSchedule = schedule && upper(schedule?.type) !== 'FLEXIBLE' &&
    upper(schedule?.type) !== 'FLEX' && upper(schedule?.type) !== 'LIBRE' &&
    startMin !== null && endMin !== null;

  if (!fixedSchedule) return false;

  const crossesMidnight = Boolean(schedule.crossesMidnight) || endMin <= startMin;
  if (!crossesMidnight) return false;

  // Para una jornada nocturna, la Entrada del día anterior sigue vigente
  // únicamente durante la ventana que termina hoy.
  const previousDay = new Date(`${nowParts.dateKey}T00:00:00Z`);
  previousDay.setUTCDate(previousDay.getUTCDate() - 1);
  const previousDateKey = previousDay.toISOString().slice(0, 10);
  if (attendanceParts.dateKey !== previousDateKey) return false;

  const nowWall = wallMinutes(nowParts.dateKey, nowParts.minutes);
  const endWall = wallMinutes(nowParts.dateKey, endMin);
  return nowWall !== null && endWall !== null && nowWall <= endWall;
};

const formatTime = timestamp => {
  try {
    return new Date(timestamp).toLocaleTimeString('es-MX', {
      timeZone: TIME_ZONE, hour: '2-digit', minute: '2-digit'
    });
  } catch (_) {
    return '--:--';
  }
};

export function resolvePersonnelStatus(user, nowValue = new Date()) {
  const now = getMeridaParts(nowValue);
  const today = now?.dateKey;
  const accountStatus = upper(user?.accountStatus);
  const profileStatus = upper(user?.profile?.status || user?.laborStatus);

  // La suspensión siempre prevalece sobre cualquier otro estado.
  if (accountStatus === 'SUSPENDIDA' || accountStatus === 'SUSPENDIDO' || profileStatus === 'SUSPENDIDO') {
    return status('suspendido', 'Suspendido', 'Cuenta o situación laboral suspendida.', { icon: '⚫' });
  }

  const marks = normalizeAttendance(user?.checkins || user?.attendance || []);
  const { openEntry: rawOpenEntry, latestPair: rawLatestPair } = findAttendanceState(marks);
  const schedule = getSchedule(user);
  const scheduleType = upper(schedule?.type);
  const isFlexible = !schedule || scheduleType === 'FLEXIBLE' || scheduleType === 'FLEX' || scheduleType === 'LIBRE';

  // Una marca histórica no debe determinar el estado actual.
  // En horarios flexibles, una jornada concluida ayer termina al cambiar el
  // día porque no existe una ventana fija que pueda prolongarla. En horarios
  // fijos, únicamente una jornada nocturna puede continuar desde ayer hasta
  // hoy, y solo mientras siga dentro de su ventana de cierre.
  const openEntry = attendanceBelongsToCurrentWorkday(rawOpenEntry, now, schedule)
    ? rawOpenEntry
    : null;
  const latestPair = rawLatestPair &&
    attendanceBelongsToCurrentWorkday(rawLatestPair.entry, now, schedule)
    ? rawLatestPair
    : null;

  // Un permiso/vacaciones/incapacidad aprobado representa una ausencia
  // autorizada cuando no existe una jornada efectiva registrada que deba
  // mostrarse como asistencia. Si la persona ya checó Entrada, la marca real
  // prevalece y se muestra como jornada en curso.
  const leave = findActiveLeave(user, today);
  if (leave && !openEntry && !latestPair) {
    const type = upper(leave.type);
    if (type === 'VACACIONES') {
      return status('vacaciones', 'Vacaciones', `Vacaciones autorizadas${leave.reason ? ` · ${leave.reason}` : ''}.`, { icon: '🔵', leave });
    }
    if (type === 'INCAPACIDAD') {
      return status('incapacidad', 'Incapacidad', `Incapacidad autorizada${leave.reason ? ` · ${leave.reason}` : ''}.`, { icon: '🌸', leave });
    }
    return status('permiso', 'Permiso', `Permiso autorizado${leave.reason ? ` · ${leave.reason}` : ''}.`, { icon: '🌸', leave });
  }

  // -----------------------------------------------------------------------
  // JORNADA ABIERTA
  // -----------------------------------------------------------------------
  // Regla importante: una Entrada válida significa que la persona está
  // laborando. Nunca debe volver a caer en "Fuera de jornada" solamente
  // porque el horario sea flexible o porque la hora de Entrada no coincida
  // con una ventana fija.
  if (openEntry) {
    const closure = findClosureForEntry(user, openEntry);
    if (closure && upper(closure.closureType) === 'ADMINISTRATIVA') {
      return status(
        'cierre_administrativo',
        'Cierre Administrativo',
        `Jornada cerrada administrativamente${closure.reason ? ` · ${closure.reason}` : ''}.`,
        { icon: '🟣', closure, latest: openEntry }
      );
    }

    // En jornada flexible no existe un horario fijo de Entrada/Salida que
    // permita hablar de Retardo o Salida anticipada. Mientras haya Entrada
    // abierta y no exista cierre administrativo, el estado es Laborando.
    if (isFlexible) {
      return status(
        'laborando',
        'Laborando',
        `Entrada ${formatTime(openEntry.timestamp)} registrada; jornada en curso.`,
        { icon: '🟢', latest: openEntry, flexible: true }
      );
    }

    const window = scheduleWindowForEntry(schedule, openEntry);
    const nowParts = getMeridaParts(nowValue);
    const nowWall = nowParts ? wallMinutes(nowParts.dateKey, nowParts.minutes) : null;

    // En una jornada estricta/fija, un empleado que ya checó Entrada pero
    // llegó después de la tolerancia debe aparecer como Retardo aunque la
    // Salida todavía no exista.
    if (window) {
      const entryParts = getMeridaParts(openEntry.timestamp);
      const entryWall = entryParts ? wallMinutes(entryParts.dateKey, entryParts.minutes) : null;

      // Una vez superado el cierre permitido, el estado actual es Jornada
      // extendida aunque la Entrada original haya sido tardía.
      if (nowWall !== null && nowWall > window.endAllowedWall) {
        return status(
          'jornada_extendida',
          'Jornada extendida',
          `Entrada ${formatTime(openEntry.timestamp)} · aún no registra Salida y ya superó el horario establecido más la tolerancia.`,
          { icon: '🟠', latest: openEntry, window }
        );
      }

      if (entryWall !== null && entryWall > window.startAllowedWall) {
        return status(
          'retardo',
          'Retardo',
          `Entrada ${formatTime(openEntry.timestamp)} · horario establecido ${String(schedule.startTime).slice(0, 5)}.`,
          { icon: '🟡', latest: openEntry, window }
        );
      }
    }

    return status(
      'laborando',
      'Laborando',
      `Entrada ${formatTime(openEntry.timestamp)} registrada; jornada en curso.`,
      { icon: '🟢', latest: openEntry, window }
    );
  }

  // -----------------------------------------------------------------------
  // JORNADA CONCLUIDA
  // -----------------------------------------------------------------------
  if (latestPair) {
    const window = scheduleWindowForEntry(schedule, latestPair.entry);

    // En flexible, la conclusión depende de que exista la pareja Entrada +
    // Salida, no de una hora fija. Por ello una Salida válida siempre concluye
    // la jornada en verde, salvo que exista una regla administrativa explícita.
    if (isFlexible || !window) {
      return status(
        'jornada_concluida',
        'Jornada concluida',
        `Entrada ${formatTime(latestPair.entry.timestamp)} · Salida ${formatTime(latestPair.exit.timestamp)}.`,
        { icon: '🟢', latest: latestPair.exit, pair: latestPair, flexible: isFlexible }
      );
    }

    const entryParts = getMeridaParts(latestPair.entry.timestamp);
    const exitParts = getMeridaParts(latestPair.exit.timestamp);
    const entryWall = entryParts ? wallMinutes(entryParts.dateKey, entryParts.minutes) : null;
    const exitWall = exitParts ? wallMinutes(exitParts.dateKey, exitParts.minutes) : null;

    if (exitWall !== null && exitWall < window.endWall) {
      return status(
        'salida_anticipada',
        'Salida anticipada',
        `Salida ${formatTime(latestPair.exit.timestamp)} · horario establecido ${String(schedule.endTime).slice(0, 5)}.`,
        { icon: '🟡', latest: latestPair.exit, pair: latestPair, window }
      );
    }

    if (entryWall !== null && entryWall > window.startAllowedWall) {
      return status(
        'retardo',
        'Retardo',
        `Entrada ${formatTime(latestPair.entry.timestamp)} · horario establecido ${String(schedule.startTime).slice(0, 5)}.`,
        { icon: '🟡', latest: latestPair.entry, pair: latestPair, window }
      );
    }

    return status(
      'jornada_concluida',
      'Jornada concluida',
      `Entrada ${formatTime(latestPair.entry.timestamp)} · Salida ${formatTime(latestPair.exit.timestamp)}.`,
      { icon: '🟢', latest: latestPair.exit, pair: latestPair, window }
    );
  }

  // -----------------------------------------------------------------------
  // SIN JORNADA ACTIVA
  // -----------------------------------------------------------------------
  // En flexible no hay una ventana horaria que pueda convertir el estado en
  // "Fuera de jornada". Si no hay Entrada, entonces sí está fuera de jornada.
  if (schedule && now) {
    const startMin = parseTime(schedule.startTime);
    const endMin = parseTime(schedule.endTime);
    const workDays = String(schedule.workDays || '').split(',').map(x => x.trim()).filter(Boolean);
    const isWorkDay = !workDays.length || workDays.includes(dayCode(now.dayOfWeek));
    if (startMin !== null && endMin !== null && isWorkDay) {
      const nowWall = wallMinutes(now.dateKey, now.minutes);
      const crossesMidnight = Boolean(schedule.crossesMidnight) || endMin <= startMin;
      const startWall = wallMinutes(now.dateKey, startMin);
      const endWall = wallMinutes(now.dateKey, endMin) + (crossesMidnight ? 1440 : 0);
      const toleranceEntry = Math.max(0, Number(schedule.toleranceEntryMinutes ?? schedule.toleranceMinutes ?? 0));
      const toleranceExit = Math.max(0, Number(schedule.toleranceExitMinutes ?? schedule.toleranceMinutes ?? 0));
      const inside = crossesMidnight
        ? nowWall >= startWall - toleranceEntry || nowWall <= endWall + toleranceExit
        : nowWall >= startWall - toleranceEntry && nowWall <= endWall + toleranceExit;
      if (!inside) {
        return status('fuera_jornada', 'Fuera de jornada', 'Actualmente fuera del horario laboral configurado.', { icon: '⚪' });
      }
    }
  }

  return status('fuera_jornada', 'Fuera de jornada', 'Sin una jornada activa registrada.', { icon: '⚪' });
}

export const PERSONNEL_STATUS_LEGEND = [
  { key: 'laborando', label: 'Laborando', color: PERSONNEL_STATUS_COLORS.laborando, icon: '🟢' },
  { key: 'jornada_concluida', label: 'Jornada concluida', color: PERSONNEL_STATUS_COLORS.jornada_concluida, icon: '🟢' },
  { key: 'salida_anticipada', label: 'Salida anticipada', color: PERSONNEL_STATUS_COLORS.salida_anticipada, icon: '🟡' },
  { key: 'retardo', label: 'Retardo', color: PERSONNEL_STATUS_COLORS.retardo, icon: '🟡' },
  { key: 'jornada_extendida', label: 'Jornada extendida', color: PERSONNEL_STATUS_COLORS.jornada_extendida, icon: '🟠' },
  { key: 'cierre_administrativo', label: 'Cierre Administrativo', color: PERSONNEL_STATUS_COLORS.cierre_administrativo, icon: '🟣' },
  { key: 'vacaciones', label: 'Vacaciones', color: PERSONNEL_STATUS_COLORS.vacaciones, icon: '🔵' },
  { key: 'incapacidad', label: 'Incapacidad', color: PERSONNEL_STATUS_COLORS.incapacidad, icon: '🌸' },
  { key: 'permiso', label: 'Permiso', color: PERSONNEL_STATUS_COLORS.permiso, icon: '🌸' },
  { key: 'suspendido', label: 'Suspendido', color: PERSONNEL_STATUS_COLORS.suspendido, icon: '⚫' },
  { key: 'fuera_jornada', label: 'Fuera de jornada', color: PERSONNEL_STATUS_COLORS.fuera_jornada, icon: '⚪' }
];
